<script setup lang="ts">
    import { useMeilisearch } from '../../../composables/useMeilisearch';
    import { useEvent } from '@/composables/useEventBus';
    import { ModelType } from '~~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~~/app/factories/EndpointFactory';
    import ContactCollection from '~~/app/contact/collections/ContactCollection';
    import ContactEndpoint from '~~/app/contact/endpoints/WorskiteEndpoint';
    import Contact from '~~/app/contact/models/Contact';
    import { CONTACT_TYPE } from '~/app/contact/enums/ContactType';
    const emit = defineEmits(['selected', 'unselect']);

    interface Props {
        contactType: typeof CONTACT_TYPE.CLIENT | typeof CONTACT_TYPE.SUPPLIER;
        multiple?: boolean;
        initialValue?: Contact | string | null;
        closeOnSelect?: boolean;
        submenuPosition?: 'left-0' | 'right-0';
        withCreateButton?: boolean;
        disabled?: boolean;
        filledByAi?: boolean;
        placeholder?: string;
    }
    const props = withDefaults(defineProps<Props>(), {
        multiple: false,
        initialValue: null,
        closeOnSelect: true,
        submenuPosition: 'left-0',
        withCreateButton: false,
        disabled: false,
        filledByAi: false,
        placeholder: undefined,
    });

    const loading = ref(true);
    const submenu = ref(null);
    const contacts: Ref<ContactCollection | null> = ref(null);
    const selected: Ref<Contact | null> = ref(props.initialValue);
    if (!selected.value) {
        selected.value = null;
    }
    const query: Ref<string> = ref('');

    const isSelected = (contact: any) => {
        return selected.value && selected.value.getId() == contact.uuid;
    };

    const select = async (contactUuid: string | null) => {
        if (!contactUuid) return;
        await getContact(contactUuid);
        emit('selected', selected.value);
        if (props.closeOnSelect) {
            submenu.value?.hide();
        }
    };

    const toggle = (contact: Contact | null) => {
        if (contact?.isNotSnapshot() || !contact) {
            submenu.value?.toggle();
        }
    };

    const show = () => {
        if (!submenu.value.isDisplayed) {
            submenu.value.show();
        }
    };

    const setValue = (contact: Contact) => {
        selected.value = contact;
    };
    const { search } = useMeilisearch();
    const meilisearchContacts = ref();
    const searchContacts = async (e: Event) => {
        loading.value = true;

        const results = await search({
            model: 'contacts',
            query: query.value,
            filter: `contact_type = ${props.contactType}`,
        });
        // should define type
        meilisearchContacts.value = results.hits;
        loading.value = false;
    };

    const displayName = (contact: any) => {
        return contact.company_name ? contact.company_name : `${contact.first_name ?? ''} ${contact.last_name ?? ''}`;
    };

    const getContact = async (contactUuid: string) => {
        loading.value = true;
        const endpoint = EndpointFactory.make(ModelType.CONTACTS) as ContactEndpoint;
        const response = await endpoint.setInclude('defaultVatRate').retrieve(contactUuid);
        if (response.error) {
            useToasteoError();
            submenu.value?.hide();
            return;
        }
        selected.value = response.data;
        loading.value = false;
    };

    useListen('contact:contact:created', (contact) => {
        if (props.withCreateButton) {
            select(contact.getId());
        }
    });

    useListen('contact:contact:auto-select', (contactId) => {
        select(contactId);
    });

    defineExpose({
        show,
        setValue,
    });

    onMounted(async () => {
        if (props.initialValue && typeof props.initialValue !== 'string' && typeof props.initialValue?.isModelOrCollection === 'function') {
            selected.value = props.initialValue;
            return;
        }
        if (typeof props.initialValue === 'string' && props.initialValue !== '') {
            console.log('In a contact select with an initialValue of string', props.initialValue);
            const { load, model } = useModelLoader(ModelType.CONTACTS, props.initialValue);
            await load();
            if (model.value) {
                selected.value = model.value;
            }
        }
    });
</script>

<template>
    <Submenu
        ref="submenu"
        :position="submenuPosition"
        :container="$theme('submenu.container.fullWidth')"
        :class="{ 'pointer-events-none': disabled }"
        max-height="max-h-[400px]"
        @shown="searchContacts"
    >
        <template #default="">
            <!-- Empty state: search-like field + "ou" + Créer (matches new design) -->
            <div
                v-if="!selected"
                class="flex items-center gap-2"
            >
                <div
                    class="flex items-center flex-1 min-w-0 px-3 transition-all bg-white border rounded-md cursor-pointer group h-9 hover:border-gray-300"
                    :class="filledByAi ? $theme('form.filled_ai') : 'border-gray-200'"
                    @click.prevent="toggle(null)"
                >
                    <i class="text-sm fa-regular fa-magnifying-glass text-neutral-400 group-hover:text-neutral-600"></i>
                    <span class="ml-2 text-sm truncate text-neutral-400">
                        {{ placeholder ?? $t('selects.contact.placeholder') }}
                    </span>
                </div>
                <template v-if="withCreateButton">
                    <span class="text-sm text-neutral-500">{{ $t('misc.or') }}</span>
                    <a
                        href="#"
                        class="flex items-center gap-1.5 h-9 px-3 text-sm transition-all bg-white border border-gray-200 rounded-md shadow-sm shrink-0 text-neutral-700 hover:bg-neutral-50"
                        @click.stop.prevent="
                            useEvent('contact:contact:form-modal:open', {
                                fields: { contactType },
                            })
                        "
                    >
                        <i class="fa-regular fa-user-plus"></i>
                        <span>{{ $t('actions.create') }}</span>
                    </a>
                </template>
            </div>
            <!-- Selected state -->
            <div
                v-else
                class="flex items-stretch justify-between px-3 py-2 transition-all border rounded"
                :class="[
                    $theme('form.select'),
                    filledByAi ? $theme('form.filled_ai') : 'border-gray-200',
                    selected?.isNotSnapshot() ? 'hover:bg-neutral-100 cursor-pointer active:bg-neutral-200 group' : 'cursor-default bg-gray-100',
                ]"
                @click.prevent="toggle(selected)"
            >
                <div class="flex justify-between w-full">
                    <div class="flex items-center space-x-2">
                        <div>
                            <i class="transition-all text-neutral-600 group-hover:text-black fa-regular fa-user-tie"></i>
                        </div>
                        <div>
                            <span
                                class="text-sm transition-all text-neutral-500 group-hover:text-black break-all truncate"
                                v-text="$truncate(selected.displayName, 50)"
                            ></span>
                        </div>
                    </div>
                    <span
                        v-if="selected.displayName && selected.isProfessional"
                        class="flex-none pr-4"
                    >
                        <CommonInfoBadge :text="$t('app.common.badges_info.pro')" />
                    </span>
                </div>
                <div>
                    <slot name="button"></slot>
                </div>
            </div>
        </template>

        <template #submenu>
            <div class="w-full min-w-[260px]">
                <p class="mb-2">
                    {{ $t('selects.contact.title') }}
                </p>
                <div class="relative mb-2">
                    <input
                        v-model="query"
                        type="search"
                        autocomplete="off"
                        class="w-full py-2 pl-4 pr-8 text-sm rounded-lg bg-neutral-100"
                        :placeholder="$t('contact.contacts.search_input_placeholder')"
                        @input.prevent.stop="searchContacts($event)"
                    />
                    <div class="absolute top-0 bottom-0 right-0 flex items-center pr-2">
                        <i
                            class="text-sm transition-all cursor-pointer text-neutral-600 fa-regular fa-search hover:text-black"
                            @click.prevent.stop="searchContacts($event)"
                        ></i>
                    </div>
                </div>
                <div class="relative">
                    <div
                        v-if="loading"
                        class="py-6"
                    >
                        <Loader></Loader>
                    </div>
                    <template v-else>
                        <SubmenuEmptyContent v-if="!meilisearchContacts.length" />
                        <div
                            v-else
                            class="flex flex-col"
                        >
                            <!-- // when select should trigger show  as now we display results from meilisearch index-->
                            <div
                                v-for="meilisearchContact in meilisearchContacts"
                                :key="meilisearchContact.uuid"
                                :class="[$theme('contextmenu.link', 'cursor-pointer'), { 'bg-neutral-100': isSelected(meilisearchContact) }]"
                                @click.stop.prevent="select(meilisearchContact.uuid)"
                                @contextmenu.prevent="
                                    ($event) =>
                                        useMeilisearchHitContextMenu({
                                            ModelType: ModelType.CONTACTS,
                                            uuid: meilisearchContact.uuid,
                                            event: $event,
                                        })
                                "
                            >
                                <div class="w-full flex justify-between">
                                    <span class="text-sm max-w-36 truncate">{{ `${displayName(meilisearchContact)}` }}</span>
                                    <span class="flex-none pr-4">
                                        <CommonProfessionalMeilisearchContact :meilisearch-contact="meilisearchContact">
                                            <CommonInfoBadge :text="$t('app.common.badges_info.pro')" />
                                        </CommonProfessionalMeilisearchContact>
                                    </span>
                                </div>
                                <div
                                    v-if="isSelected(meilisearchContact)"
                                    class="absolute top-0 bottom-0 right-0 flex items-center pr-3"
                                >
                                    <a
                                        href="#"
                                        @click.prevent.stop="emit('unselect')"
                                    >
                                        <i class="text-neutral-400 fa-regular fa-xmark hover:text-neutral-600"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </Submenu>
</template>
